/*
 * jc-ship-date.js - when the parcel leaves the warehouse. The one calculation
 * behind the delivery line under the buy button (assets/product-delivery-line.js)
 * and the countdown in the cart (assets/cart-countdown.js), so both always name
 * the same day.
 *
 * The source is Cortex (window.JapCortex.ready): the ship date and the cut-off
 * moment for the visitor's country (levering.verzenddatum and
 * besteltijd_moment, and na_besteltijd once that moment has passed), else
 * Cortex's cut-off time and shipping days. While Cortex is silent there is no
 * ship date: Cortex moves the cut-off per period, so a fixed time in the theme
 * would promise the wrong day. Nothing here is a promise of its own.
 *
 * A ship date is { date: 'YYYY-MM-DD', offset: days from today, weekday:
 * 0 = Sunday, deadline: epoch ms of the cut-off that still makes that date }.
 * Everything is computed in the shop's time zone, so a visitor abroad reads
 * the same promise the warehouse keeps.
 */
window.JapShipDate =
  window.JapShipDate ||
  (() => {
    /** Today's date (YYYY-MM-DD), weekday (0 = Sunday) and time (HH:MM) in a time zone. */
    function now(timeZone) {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        weekday: 'short',
        hour12: false,
      }).formatToParts(new Date());
      const get = (type) => (parts.find((part) => part.type === type) || {}).value || '';
      const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
      return {
        date: `${get('year')}-${get('month')}-${get('day')}`,
        weekday,
        time: `${get('hour').replace('24', '00')}:${get('minute')}`,
      };
    }

    function addDays(date, days) {
      const d = new Date(`${date}T12:00:00Z`);
      d.setUTCDate(d.getUTCDate() + days);
      return d.toISOString().slice(0, 10);
    }

    function daysBetween(from, to) {
      return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86400000);
    }

    /** Epoch ms of a wall-clock time (HH:MM) on a date in a time zone. */
    function moment(date, time, timeZone) {
      const [year, month, day] = date.split('-').map(Number);
      const [hour, minute] = time.split(':').map(Number);
      const asUtc = Date.UTC(year, month - 1, day, hour, minute);
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).formatToParts(new Date(asUtc));
      const get = (type) => Number((parts.find((part) => part.type === type) || {}).value);
      const zoneOffset = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute')) - asUtc;
      return asUtc - zoneOffset;
    }

    /**
     * The ship date from the cut-off, the shipping days (ISO weekday 1-7,
     * Sunday as 7 or 0) and the closed days (dates): today before the cut-off
     * on a shipping day, otherwise the next shipping day that is not closed.
     */
    function shipDateFrom(cutoff, shipDays, closedDays, timeZone) {
      if (!cutoff) return null;
      const today = now(timeZone);
      const days = new Set(shipDays.map((day) => Number(day) % 7));
      const closed = new Set((closedDays || []).map((day) => String(day).trim()).filter(Boolean));
      if (days.size === 0) return null;
      const ships = (date, weekday) => days.has(weekday) && !closed.has(date);
      let offset = 0;
      let weekday = today.weekday;
      if (!(ships(today.date, weekday) && today.time < cutoff)) {
        offset = 1;
        weekday = (weekday + 1) % 7;
        while (!ships(addDays(today.date, offset), weekday) && offset < 14) {
          offset += 1;
          weekday = (weekday + 1) % 7;
        }
      }
      const date = addDays(today.date, offset);
      return { date, offset, weekday, deadline: moment(date, cutoff, timeZone) };
    }

    /**
     * The ship date Cortex promises the visitor's country, or null. Cortex's
     * answer may be a few minutes old: past its cut-off moment the "after the
     * cut-off" branch applies, and past that one too (an old answer) the
     * date is computed from Cortex's cut-off time and shipping days.
     * fallback: { timeZone, shipDays } for what Cortex leaves out.
     */
    function fromCortex(state, fallback = {}) {
      if (!state) return null;
      const option = state.option || {};
      const timeZone = (state.payload && state.payload.tijdzone) || fallback.timeZone;
      const passed = (promise) => promise && promise.besteltijd_moment && Date.parse(promise.besteltijd_moment) <= Date.now();
      let delivery = (state.land && state.land.levering) || option.levering || null;
      if (passed(delivery)) delivery = delivery.na_besteltijd || null;
      if (delivery && delivery.verzenddatum && !passed(delivery)) {
        const date = delivery.verzenddatum;
        return {
          date,
          offset: daysBetween(now(timeZone).date, date),
          weekday: new Date(`${date}T12:00:00Z`).getUTCDay(),
          deadline: delivery.besteltijd_moment ? Date.parse(delivery.besteltijd_moment) : null,
        };
      }
      if (option.besteltijd) {
        const shipDays = (state.payload && state.payload.verzenddagen) || fallback.shipDays || [];
        return shipDateFrom(option.besteltijd, shipDays, [], timeZone);
      }
      return null;
    }

    return { now, shipDateFrom, fromCortex };
  })();
